import RevenueArrangementProcessor from "./revenueArrangement/RevenueArrangementProcessor";
import RevenueArrangementContext from "./revenueArrangement/RevenueArrangementContext";
import RevenueArrangementInitializer from "./revenueArrangement/RevenueArrangementInitializer";
import RevenueArrangementUtils from "./revenueArrangement/RevenueArrangementUtils";
import * as ExpenseArrangementHelper from "./ExpenseArrangementHelper";
import { Utils } from "revlock-webutils";

/**
 * Class to manage the population of Revenue Arrangements for a Sales Order.
 * This class initializes the context, processes each Revenue Arrangement,
 * calculates total annual booking, and handles expense processing.
 * @class RevenueEngine
 * @constructor
 * @param {Object} salesOrder - The Sales Order object to process.
 * @param {Object} clientReferenceData - Client reference data for processing.
 * @param {Array} errors - Array to collect errors during processing.
 * @param {boolean} isApiCall - Flag indicating if the call is from an API.
 * @method populate - Main method to orchestrate the population of Revenue Arrangements.
 * @method postProcess - Method to calculate total annual booking and populate client attributes.
 * @method processExpense - Method to handle expense processing for the Sales Order.
 * @returns {void}
 * @throws {Error} Throws an error if processing fails.
 * @example
 * const revenueEngine = new RevenueEngine(salesOrder, clientReferenceData, errors, isApiCall);
 * revenueEngine.populate();
 */
export default class RevenueEngine {
    constructor(salesOrder, clientReferenceData, errors, isApiCall) {
        this.salesOrder = salesOrder;
        this.context = new RevenueArrangementContext(
            clientReferenceData,
            errors,
            isApiCall
        );
        this.initializer = new RevenueArrangementInitializer(
            salesOrder,
            this.context
        );
    }

    populate() {
        const salesOrder = this.salesOrder;
        const { clientReferenceData, orgConfig, options, errors } =
            this.context;

        try {
            // 0. Check if there are any Sales Order Items
            if (
                !salesOrder.salesOrderItem ||
                salesOrder.salesOrderItem.length === 0
            )
                return;

            // 1. Prepare Revenue Arrangement
            this.initializer.prepareRevenueArrangements();

            // 1.a Check if there are any Revenue Arrangements
            if (this.salesOrder.revenueArrangement.length === 0) return;

            // 2. Process each Revenue Arrangement
            this.salesOrder.revenueArrangement.forEach((ra) => {
                const processor = new RevenueArrangementProcessor(
                    this.salesOrder,
                    ra,
                    this.context
                );
                processor.process();
            });

            // 3. Calculate Total Annual Booking
            this.postProcess();

            // 4. Process Expenses
            this.processExpense();
        } catch (e) {
            const err = `Ran in to errors while populating revenue arrangement for so ${salesOrder.id} with error: ${e.message}, ${e.stack}`;
            throw new Error(err);
        } finally {
            if (this.salesOrder && this.salesOrder.runtime) {
                Utils.disposeRuntime(this.salesOrder);
            }
            this.salesOrder = null;
            this.context = null;
            this.initializer = null;
        }
    }

    postProcess() {
        const salesOrder = this.salesOrder;
        const { clientAttributes } = this.context;

        // Calculate Total Annual Booking
        salesOrder.totalAnnualBooking = salesOrder.salesOrderItem.reduce(
            (booking, salesOrderItem) =>
                (booking += salesOrderItem.annualExtendedSalePrice),
            0.0
        );

        RevenueArrangementUtils.populateClientAttributes(
            clientAttributes,
            ["salesOrder"],
            {
                salesOrder
            }
        );
    }

    processExpense() {
        const salesOrder = this.salesOrder;
        const { clientReferenceData, orgConfig, options, errors } =
            this.context;
        ExpenseArrangementHelper.processExpense(
            clientReferenceData,
            salesOrder,
            errors,
            orgConfig,
            options
        );
    }
}
